<script setup>
  import { computed } from 'vue'
  import BcsLinearProgress from '../common/BcsLinearProgress.vue'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import checkGrayIcon from '@/assets/images/bcst-check-gray.svg'

  // Named presets define bar height only — width is always controlled by the parent container.
  // Pass size as an object { width, height } when an explicit width is also needed.
  const SIZE_PRESETS = {
    sm: { height: '9px'     },
    md: { height: '13.01px' },
    lg: { height: '22px'    },
  }

  const props = defineProps({
    value: { type: Number, required: true },
    color: { type: String, default: 'success' },
    // Named preset ('sm' | 'md' | 'lg') or a custom object { width?, height? }.
    // When omitted, BcsLinearProgress renders at its own default height (no width constraint).
    size: { type: [String, Object], default: null },
    // Label text. Use the #label slot for rich markup.
    label: { type: String, default: null },
    labelClass: { type: [String, Object, Array], default: '' },
    // 'above': label in its own row above the bar (default).
    // 'inline': label is a flex sibling of the bar in the same row.
    labelPosition: { type: String, default: 'above' },
    // Adds "value%" to the right of the label row (only when labelPosition="above").
    showPercent: { type: Boolean, default: false },
    percentClass: { type: [String, Object, Array], default: '' },
    // 'check' renders the default check-circle icon; 'none' renders nothing.
    // Override completely with the #icon slot.
    icon: { type: String, default: 'check' },
    isComplete: { type: Boolean, default: false },
    // Controls the font-size of the check icon. Applied as inline style on the default
    // icon and exposed as --bpb-icon-size CSS variable so #icon slot content can inherit it.
    iconSize: { type: String, default: '1.1rem' },
  })

  const resolvedSize = computed(() => {
    if (!props.size) return null
    if (typeof props.size === 'string') return SIZE_PRESETS[props.size] ?? null
    return props.size
  })

  // Applied to the BcsLinearProgress element to override the default track height.
  const barStyle = computed(() =>
    resolvedSize.value?.height ? { height: resolvedSize.value.height } : {}
  )

  // Applied to .bpb-root: optional width constraint + icon size CSS variable.
  const rootStyle = computed(() => ({
    ...(resolvedSize.value?.width ? { width: resolvedSize.value.width } : {}),
    '--bpb-icon-size': props.iconSize,
  }))

  const hasLabel = computed(() => !!props.label)
  const isInline = computed(() => props.labelPosition === 'inline')
</script>

<template>
  <div class="bpb-root" :style="rootStyle">
    <!-- Label row: rendered above the bar when labelPosition="above" -->
    <div
      v-if="($slots.label || hasLabel) && !isInline"
      class="d-flex justify-content-between align-items-baseline bpb-label-row">
      <slot name="label">
        <span :class="labelClass">{{ label }}</span>
      </slot>
      <span v-if="showPercent && !$slots.label" :class="percentClass">{{ value }}%</span>
    </div>

    <!-- Bar row: when inline, label appears as a flex sibling before the bar -->
    <div class="d-flex align-items-center bpb-bar-row">
      <template v-if="isInline && hasLabel">
        <slot name="label">
          <span :class="['bpb-inline-label', labelClass]">{{ label }}</span>
        </slot>
      </template>

      <BcsLinearProgress
        :value="value"
        :color="color"
        :style="barStyle"
        class="flex-grow-1" />

      <slot name="icon">
        <img
          v-if="icon === 'check'"
          :src="isComplete ? checkGreenIcon : checkGrayIcon"
          alt=""
          class="flex-shrink-0 bpb-check-icon"
          :style="{ width: iconSize, height: iconSize }" />
      </slot>
    </div>

    <!-- Sublabel slot: any content below the bar -->
    <slot name="sublabel" />
  </div>
</template>

<style scoped lang="scss">
  .bpb-root {
    display: flex;
    flex-direction: column;
  }

  /* No default bottom margin — each consumer controls spacing via slot content or labelClass */
  .bpb-label-row {
    margin-bottom: 0;
  }

  .bpb-bar-row {
    overflow: visible;
  }

  /* Inline label: sits left of the bar in the same flex row */
  .bpb-inline-label {
    flex-shrink: 0;
    white-space: nowrap;
    margin-right: 16px;
  }

  /* Overlap the check icon on the bar end to match the visual pattern used in
     module cards and the category lessons grid. */
  .bpb-check-icon {
    margin-left: -5px;
    position: relative;
    z-index: 1;
    display: inline-block;
    line-height: 1;
    /* Size controlled by inline style from iconSize prop */
  }
</style>
